import React from 'react';

import { projectStore } from '@core/store';
import RedirectWithHash from '@core/utils/RedirectWithHash';

/**
 * Returns the route pathname for the current project's "first page". Use this
 * to redirect users when reaching a navigational dead-end.
 * @example
 * ```ts
 * const history = useHistory();
 *
 * if (noWhereElseToGo) {
 *   history.push(getProjectFirstPagePath());
 * }
 * ```
 */
export function getProjectFirstPagePath() {
  const projectFirstPage = projectStore.getState().data.appearance.navigation.first_page;

  switch (projectFirstPage) {
    case 'documentation':
      return '/docs';
    case 'reference':
      return '/reference';
    case 'landing_page':
      return '/';
    default:
      throw new Error('Missing project navigation first page field');
  }
}

/**
 * Redirects to the project's "first page" that is currently selected in the
 * `Project > Appearance > Site Navigation` settings. Typically, this is either
 * the landing page, guides section or the reference section.
 *
 * For use only on Hub routes, this is useful when you reach a navigational
 * dead-end with nowhere else to redirect to.
 *
 * @example
 * ```ts
 * // No redirect path exists and there is no last page to return to.
 * if (!redirectPath && !lastPage) {
 *   return <RedirectToProjectFirstPage />;
 * }
 * ```
 */
export default function RedirectToProjectFirstPage() {
  const projectFirstPagePath = getProjectFirstPagePath();

  return <RedirectWithHash to={projectFirstPagePath} />;
}
